import { useShow, useNavigation } from '@refinedev/core'
import { useParams } from 'react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toTitleCase } from '@/lib/utils'
import { ArrowLeft } from 'lucide-react'
import { buildDisplayColumns } from '@/components/shared/DataTable'
import { RESOURCE_READ_VIEW } from '@/resources'

export function ShowPage({ resource }: { resource: string }) {
  const { id } = useParams<{ id: string }>()
  const readResource = RESOURCE_READ_VIEW[resource] ?? resource
  const { query } = useShow({ resource: readResource, id: id ?? undefined })
  const { goBack, edit } = useNavigation()
  const record = query?.data?.data as Record<string, unknown> | undefined
  const loadFailed = query?.isError === true && !record

  const columns = record ? Object.keys(record) : []
  const { displayColumns, headerOverrides } = buildDisplayColumns(columns, {})

  if (!id) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">Missing record id in URL.</p>
        <Button variant="outline" size="sm" onClick={() => goBack()}>
          Go back
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => goBack()}>
          <ArrowLeft size={16} />
        </Button>
        <h1 className="text-xl font-semibold">{toTitleCase(resource)}</h1>
        <Button
          size="sm"
          className="ml-auto bg-[#076698] hover:bg-[#055580]"
          onClick={() => record?.id && edit(resource, record.id as string)}
        >
          Edit
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-muted-foreground">
            Record #{String(record?.id ?? id ?? '—')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {query?.isLoading && (
            <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>
          )}
          {loadFailed && (
            <p className="text-sm text-destructive mb-4">
              Could not load this record.
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
            {record && displayColumns.map((key) => (
              <div key={key} className="space-y-0.5">
                <dt className="text-xs text-muted-foreground">
                  {headerOverrides[key] ?? toTitleCase(key)}
                </dt>
                <dd className="text-sm">
                  {typeof record[key] === 'boolean' ? (
                    <Badge variant={record[key] ? 'default' : 'secondary'}>
                      {record[key] ? 'Yes' : 'No'}
                    </Badge>
                  ) : record[key] !== null && typeof record[key] === 'object' ? (
                    <span className="text-muted-foreground text-xs font-mono break-all">
                      {JSON.stringify(record[key])}
                    </span>
                  ) : (
                    String(record[key] ?? '—')
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}
